import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuthToken } from '@mystudioclass/auth';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import socketService from '../services/socket';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { getToken, isLoaded: tokenLoaded } = useAuthToken();
  // Use CustomerAuthContext to check if user has completed registration (has DB record)
  const { isAuthenticated, loading: authLoading } = useCustomerAuth();

  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const queryClient = useQueryClient();
  const tokenFetchedRef = useRef(false);

  // Fetch Better Auth token only when fully authenticated (Better Auth + DB record)
  useEffect(() => {
    const fetchToken = async () => {
      if (authLoading || !tokenLoaded) return;

      // Only get token if user is fully authenticated (has DB record)
      if (isAuthenticated && !tokenFetchedRef.current) {
        try {
          const token = await getToken();
          setAuthToken(token);
          tokenFetchedRef.current = true;
        } catch (error) {
          console.error('Failed to get auth token:', error);
          setAuthToken(null);
        }
      } else if (!isAuthenticated) {
        setAuthToken(null);
        tokenFetchedRef.current = false;
      }
    };

    fetchToken();
  }, [isAuthenticated, authLoading, tokenLoaded, getToken]);

  useEffect(() => {
    if (!authToken) {
      // Disconnect if previously connected
      if (socketService.isConnected()) {
        console.log('User not fully authenticated - disconnecting WebSocket');
        socketService.disconnect();
        setIsConnected(false);
      }
      return;
    }

    // Connect to WebSocket if not already connected
    if (!socketService.isConnected()) {
      console.log('🔌 Connecting to WebSocket with token...');
      socketService.connect(authToken);
    } else {
      console.log('Socket already connected, registering listeners...');
    }

    // Setup connection status listeners
    const handleConnect = () => {
      console.log('✅ Socket connected');
      setIsConnected(true);
      setConnectionError(null);
    };

    const handleDisconnect = ({ reason }) => {
      console.log('❌ Socket disconnected:', reason);
      setIsConnected(false);
      if (reason === 'io server disconnect') {
        // Server disconnected us - might need to re-authenticate
        setConnectionError('Server disconnected');
      }
    };

    // Check if mobile - don't show connection toasts on mobile since they're confusing
    // and HTTP still works fine for all operations
    const isMobile = window.innerWidth < 768;

    const handleError = ({ error, attempts }) => {
      console.error('Socket error:', error);
      setConnectionError(error);
      // Only show toast on desktop after many attempts
      if (!isMobile && attempts >= 8) {
        toast.error('Connection issues. Your changes still save.', { duration: 3000 });
      }
    };

    const handleReconnected = ({ attemptNumber }) => {
      console.log('🔄 Socket reconnected after', attemptNumber, 'attempts');
      setIsConnected(true);
      setConnectionError(null);
      // Only show toast on desktop if reconnection took multiple attempts
      if (!isMobile && attemptNumber > 3) {
        toast.success('Reconnected!', { duration: 2000 });
      }

      // Refresh all data after reconnection
      queryClient.invalidateQueries();
    };

    const handleReconnectFailed = () => {
      console.error('❌ Socket reconnection failed');
      setIsConnected(false);
      setConnectionError('Connection failed');
      // Only show toast on desktop
      if (!isMobile) {
        toast.error('Live updates paused. Refresh to see latest changes.', { duration: 5000 });
      }
    };

    // Register connection listeners
    socketService.on('socket:connected', handleConnect);
    socketService.on('socket:disconnected', handleDisconnect);
    socketService.on('socket:error', handleError);
    socketService.on('socket:reconnected', handleReconnected);
    socketService.on('socket:reconnect_failed', handleReconnectFailed);

    // Setup real-time event listeners for customers
    const handleScheduleCreated = ({ schedule, studioId }) => {
      console.log('📅 New class:', schedule.video?.title || schedule.liveTitle || 'Class');

      // INSTANT: Add schedule to ALL schedule caches (use predicate for partial matching)
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'schedules' },
        (oldData) => {
          if (!oldData?.data?.schedules) return oldData;
          // Check if already exists
          const exists = oldData.data.schedules.some(s => s.id === schedule.id);
          if (exists) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              schedules: [...oldData.data.schedules, schedule]
            }
          };
        }
      );

      // FALLBACK: Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['schedules-today'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['upcoming-schedules-countdown'], refetchType: 'active' });

      toast.success(`New class available: ${schedule.video?.title || schedule.liveTitle || 'Class'}`, {
        icon: '🎉'
      });
    };

    const handleScheduleUpdated = ({ schedule, studioId }) => {
      console.log('📅 Class updated:', schedule.video?.title || schedule.liveTitle || schedule.id?.substring(0, 8));

      // Check if customer has booking BEFORE updating cache
      const myBookingsData = queryClient.getQueriesData({ predicate: (q) => q.queryKey[0] === 'my-bookings' });
      const myBookings = myBookingsData?.[0]?.[1];
      const affectsMe = myBookings?.data?.some(b => b.scheduleId === schedule.id);

      // INSTANT: Update schedule in ALL schedule caches
      // Map current_bookings to bookingCount for customer portal compatibility
      const scheduleWithBookingCount = {
        ...schedule,
        bookingCount: schedule.current_bookings ?? schedule.bookingCount
      };

      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'schedules' },
        (oldData) => {
          if (!oldData?.data?.schedules) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              schedules: oldData.data.schedules.map(s =>
                s.id === schedule.id ? { ...s, ...scheduleWithBookingCount } : s
              )
            }
          };
        }
      );

      // FALLBACK: Invalidate ALL schedule queries to ensure UI updates
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedules-today'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['upcoming-schedules-countdown'], refetchType: 'active' });

      if (affectsMe) {
        toast(`Class time changed: ${schedule.video?.title || schedule.liveTitle || 'Your class'}`, {
          duration: 6000,
          icon: '⚠️'
        });
      } else {
        toast(`Class updated: ${schedule.video?.title || schedule.liveTitle || 'Class'}`);
      }
    };

    const handleScheduleDeleted = ({ id, studioId }) => {
      console.log('📅 Class cancelled:', id?.substring(0, 8));

      // Check if customer has booking BEFORE updating cache
      const myBookingsData = queryClient.getQueriesData({ predicate: (q) => q.queryKey[0] === 'my-bookings' });
      const myBookings = myBookingsData?.[0]?.[1];
      const affectsMe = myBookings?.data?.some(b => b.scheduleId === id);

      // INSTANT: Remove schedule from ALL schedule caches
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'schedules' },
        (oldData) => {
          if (!oldData?.data?.schedules) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              schedules: oldData.data.schedules.filter(s => s.id !== id)
            }
          };
        }
      );

      // Also remove from my-bookings if affected
      if (affectsMe) {
        queryClient.setQueriesData(
          { predicate: (query) => query.queryKey[0] === 'my-bookings' },
          (oldData) => {
            if (!oldData?.data) return oldData;
            const bookings = Array.isArray(oldData.data) ? oldData.data : [];
            return { ...oldData, data: bookings.filter(b => b.scheduleId !== id) };
          }
        );
      }

      // FALLBACK: Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['schedules-today'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['upcoming-schedules-countdown'], refetchType: 'active' });

      if (affectsMe) {
        toast.error('A class you booked has been cancelled', {
          duration: 8000,
          icon: '❌'
        });
      } else {
        toast('A class has been cancelled');
      }
    };

    const handleBookingConfirmed = ({ booking }) => {
      console.log('🎫 Booking confirmed:', booking.id?.substring(0, 8));

      // INSTANT: Add booking to my-bookings cache
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'my-bookings' },
        (oldData) => {
          if (!oldData?.data) return oldData;
          const bookings = Array.isArray(oldData.data) ? oldData.data : [];
          // Check if already exists
          const exists = bookings.some(b => b.id === booking.id);
          if (exists) return oldData;
          return { ...oldData, data: [...bookings, booking] };
        }
      );

      // INSTANT: Update booking count in ALL schedule caches
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'schedules' },
        (oldData) => {
          if (!oldData?.data?.schedules) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              schedules: oldData.data.schedules.map(s => {
                if (s.id === booking.scheduleId) {
                  return { ...s, bookingCount: (s.bookingCount || 0) + 1 };
                }
                return s;
              })
            }
          };
        }
      );

      // Toast is handled by the booking page - no need to duplicate here
    };

    const handleBookingCancelled = ({ id, scheduleId, reason, message }) => {
      console.log('🎫 [1/6] Booking cancelled event received:', { id, scheduleId, reason });

      // DEBUG: Get cache state BEFORE update
      const beforeUpdate = queryClient.getQueryData(['my-bookings', undefined]) ||
                           queryClient.getQueriesData({ predicate: q => q.queryKey[0] === 'my-bookings' })?.[0]?.[1];
      console.log('🎫 [2/6] Cache BEFORE update:', {
        hasData: !!beforeUpdate?.data,
        bookingCount: beforeUpdate?.data?.length,
        bookingIds: beforeUpdate?.data?.map(b => b.id?.substring(0, 8))
      });

      // INSTANT: Remove booking from my-bookings cache
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'my-bookings' },
        (oldData) => {
          if (!oldData?.data) return oldData;
          const bookings = Array.isArray(oldData.data) ? oldData.data : [];
          const filtered = bookings.filter(b => b.id !== id);
          console.log('🎫 [3/6] setQueriesData callback - removed:', bookings.length - filtered.length);
          return { ...oldData, data: filtered };
        }
      );

      // DEBUG: Get cache state AFTER setQueriesData
      const afterSetQueries = queryClient.getQueriesData({ predicate: q => q.queryKey[0] === 'my-bookings' })?.[0]?.[1];
      console.log('🎫 [4/6] Cache AFTER setQueriesData:', {
        bookingCount: afterSetQueries?.data?.length,
        stillHasBooking: afterSetQueries?.data?.some(b => b.id === id)
      });

      // Update booking count in schedule caches
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'schedules' },
        (oldData) => {
          if (!oldData?.data?.schedules) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              schedules: oldData.data.schedules.map(s => {
                if (s.id === scheduleId) {
                  return { ...s, bookingCount: Math.max((s.bookingCount || 0) - 1, 0) };
                }
                return s;
              })
            }
          };
        }
      );

      // Force immediate refetch
      console.log('🎫 [5/6] Triggering refetchQueries...');
      queryClient.refetchQueries({ queryKey: ['my-bookings'] }).then(() => {
        const afterRefetch = queryClient.getQueriesData({ predicate: q => q.queryKey[0] === 'my-bookings' })?.[0]?.[1];
        console.log('🎫 [6/6] Cache AFTER refetch:', {
          bookingCount: afterRefetch?.data?.length,
          stillHasBooking: afterRefetch?.data?.some(b => b.id === id)
        });
      });
      queryClient.refetchQueries({ queryKey: ['schedules'] });

      // Dispatch event to clear optimistic booking state in CustomerSchedulePage
      window.dispatchEvent(new CustomEvent('booking:cancelled', {
        detail: { bookingId: id, scheduleId }
      }));
      console.log('🎫 [EXTRA] Dispatched booking:cancelled event for scheduleId:', scheduleId?.substring(0, 8));

      // Only show toast for admin/schedule-initiated cancellations
      // User-initiated cancellations already show a toast from the page
      if (reason === 'schedule_changed' || reason === 'schedule_deleted') {
        toast.error(message || 'Your booking was cancelled due to class changes', {
          duration: 8000,
          icon: '❌'
        });
      }
      // No toast for user-initiated cancellations - handled by the page
    };

    const handleBookingDetached = ({ id, scheduleId, reason, message }) => {
      // Attended booking was detached from a rescheduled class
      // The booking still exists (attendance preserved) but is no longer tied to the schedule
      console.log('🎫 Booking detached:', id?.substring(0, 8), 'from schedule', scheduleId?.substring(0, 8));

      // Refetch bookings to get the updated booking (scheduleId is now null)
      queryClient.refetchQueries({ queryKey: ['my-bookings'] });

      // Show info toast - this is not a cancellation, attendance is preserved
      toast(message || 'A class you attended was rescheduled - your attendance record is preserved', {
        duration: 6000,
        icon: 'ℹ️'
      });
    };

    const handleBookingCreated = ({ scheduleId }) => {
      // Someone else booked - update spot count instantly
      console.log('🎫 Spot taken:', scheduleId?.substring(0, 8));

      // INSTANT: Update booking count in ALL schedule caches
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'schedules' },
        (oldData) => {
          if (!oldData?.data?.schedules) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              schedules: oldData.data.schedules.map(s => {
                if (s.id === scheduleId) {
                  return { ...s, bookingCount: (s.bookingCount || 0) + 1 };
                }
                return s;
              })
            }
          };
        }
      );
    };

    const handleSchedulesBulkCreated = ({ seriesId, instanceCount, studioId }) => {
      console.log('📅 Bulk schedules:', instanceCount, 'classes');
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'schedules' || key === 'upcoming-schedules-countdown' || key === 'schedules-today';
        }
      });
      toast.success(`${instanceCount} new classes available!`, {
        icon: '🎉'
      });
    };

    const handleSeriesDeleted = ({ seriesId, instanceCount, studioId }) => {
      console.log('📅 Series cancelled:', instanceCount, 'classes');
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'schedules' || key === 'upcoming-schedules-countdown' || key === 'schedules-today';
        }
      });

      // Check if customer has bookings for any of these schedules
      const myBookings = queryClient.getQueryData(['my-bookings']);
      const hasAffectedBookings = myBookings?.data?.some(b =>
        // Check if any booking's schedule belongs to this series
        // (This is a simple check - backend should send affected booking IDs for accuracy)
        b.schedule?.seriesId === seriesId
      );

      if (hasAffectedBookings) {
        toast.error(`${instanceCount} classes you booked have been cancelled`, {
          duration: 8000,
          icon: '❌'
        });
        // Refresh bookings to show cancellations
        queryClient.invalidateQueries(['my-bookings']);
      } else {
        toast(`${instanceCount} classes have been cancelled`);
      }
    };

    const handleSeriesUpdated = ({ seriesId, instanceCount, studioId, partial }) => {
      console.log('📅 Series updated:', instanceCount, 'classes');
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'schedules' || key === 'upcoming-schedules-countdown' || key === 'schedules-today';
        }
      });

      // Check if customer has bookings for any of these schedules
      const myBookings = queryClient.getQueryData(['my-bookings']);
      const hasAffectedBookings = myBookings?.data?.some(b =>
        b.schedule?.seriesId === seriesId
      );

      if (hasAffectedBookings) {
        toast(`${instanceCount} classes you booked have been updated`, {
          duration: 6000,
          icon: '⚠️'
        });
        queryClient.invalidateQueries(['my-bookings']);
      } else {
        toast(`${instanceCount} classes have been updated`);
      }
    };

    // Handle video updates (video info is embedded in schedule data)
    const handleVideoUpdated = ({ video }) => {
      console.log('🎥 Video updated:', video.id, video.title);
      // Invalidate schedules since video data is embedded in schedule responses
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'schedules' });
      queryClient.refetchQueries({ predicate: (query) => query.queryKey[0] === 'schedules' });
    };

    // Handle video synced from desktop app (single video)
    const handleVideoSynced = ({ video }) => {
      console.log('🎥 Video synced:', video.id);
      // Invalidate schedules since video data is embedded in schedule responses
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'schedules' });
      queryClient.refetchQueries({ predicate: (query) => query.queryKey[0] === 'schedules' });
    };

    // Handle bulk video sync from desktop app
    const handleVideosSynced = ({ synced, deleted, results }) => {
      console.log(`🎥 Bulk video sync: ${synced} synced, ${deleted} deleted`);
      // Invalidate schedules since video data is embedded in schedule responses
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'schedules' });
      queryClient.refetchQueries({ predicate: (query) => query.queryKey[0] === 'schedules' });
    };

    // Handle check-in success (from another device/session)
    const handleCheckinSuccess = ({ bookingId, scheduleId, checkedInAt }) => {
      console.log('✅ Check-in confirmed:', bookingId?.substring(0, 8));

      // INSTANT: Update the booking's checkedInAt in my-bookings cache
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'my-bookings' },
        (oldData) => {
          if (!oldData?.data) return oldData;
          const bookings = Array.isArray(oldData.data) ? oldData.data : [];
          return {
            ...oldData,
            data: bookings.map(b =>
              b.id === bookingId ? { ...b, checkedInAt } : b
            )
          };
        }
      );

      // Invalidate weekly-goal to update the ring
      queryClient.invalidateQueries({ queryKey: ['weekly-goal'] });

      // Refetch my-bookings for dashboard stats
      queryClient.refetchQueries({ queryKey: ['my-bookings'] });
    };

    // Register event listeners
    socketService.on('schedule:created', handleScheduleCreated);
    socketService.on('schedule:updated', handleScheduleUpdated);
    socketService.on('schedule:deleted', handleScheduleDeleted);
    socketService.on('schedules:bulk_created', handleSchedulesBulkCreated);
    socketService.on('schedules:series_deleted', handleSeriesDeleted);
    socketService.on('schedules:series_updated', handleSeriesUpdated);
    socketService.on('booking:confirmed', handleBookingConfirmed); // Customer-specific
    socketService.on('booking:cancelled', handleBookingCancelled); // Customer-specific
    socketService.on('booking:detached', handleBookingDetached); // Customer-specific - attended booking detached from rescheduled class
    socketService.on('booking:created', handleBookingCreated); // Update spot counts
    socketService.on('video:updated', handleVideoUpdated); // Video metadata changed
    socketService.on('video:synced', handleVideoSynced); // Desktop app synced a video
    socketService.on('videos:synced', handleVideosSynced); // Desktop app bulk synced videos
    socketService.on('checkin:success', handleCheckinSuccess); // Check-in from another device

    // Cleanup on unmount - remove listeners but keep socket connected
    return () => {
      socketService.off('socket:connected', handleConnect);
      socketService.off('socket:disconnected', handleDisconnect);
      socketService.off('socket:error', handleError);
      socketService.off('socket:reconnected', handleReconnected);
      socketService.off('socket:reconnect_failed', handleReconnectFailed);

      socketService.off('schedule:created', handleScheduleCreated);
      socketService.off('schedule:updated', handleScheduleUpdated);
      socketService.off('schedule:deleted', handleScheduleDeleted);
      socketService.off('schedules:bulk_created', handleSchedulesBulkCreated);
      socketService.off('schedules:series_deleted', handleSeriesDeleted);
      socketService.off('schedules:series_updated', handleSeriesUpdated);
      socketService.off('booking:confirmed', handleBookingConfirmed);
      socketService.off('booking:cancelled', handleBookingCancelled);
      socketService.off('booking:detached', handleBookingDetached);
      socketService.off('booking:created', handleBookingCreated);
      socketService.off('video:updated', handleVideoUpdated);
      socketService.off('video:synced', handleVideoSynced);
      socketService.off('videos:synced', handleVideosSynced);
      socketService.off('checkin:success', handleCheckinSuccess);

      // Don't disconnect - let the socket persist for the session
      // Only disconnect when token is removed (handled in the authToken check above)
    };
  }, [queryClient, authToken]);

  const value = {
    socket: socketService,
    isConnected,
    connectionError
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
